'use client';

import { useEffect, useState } from 'react';
import type { Participant } from '@/types/session';
import { createLogger } from '@/lib/logger';

const logger = createLogger('[ParticipantList]', 'session');

interface ParticipantListProps {
  sessionId: string;
}

// Demo participants for template
const demoParticipants: Participant[] = [
  { id: '1', name: 'Demo User', joinedAt: new Date().toISOString(), status: 'active' },
];

export default function ParticipantList({ sessionId }: ParticipantListProps) {
  const [participants, setParticipants] = useState<Participant[]>(demoParticipants);

  useEffect(() => {
    // In production, this would subscribe to real participant updates via WebSocket
    logger.log(`Watching session: ${sessionId}`);
  }, [sessionId]);

  const activeCount = participants.filter((p) => p.status === 'active').length;

  return (
    <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
      <h3 className="font-semibold mb-3 flex items-center justify-between">
        <span>Participants</span>
        <span className="text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
          {activeCount} active
        </span>
      </h3>

      <ul className="space-y-2">
        {participants.map((participant) => (
          <li
            key={participant.id}
            className="flex items-center gap-2 text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded"
          >
            <span
              className={`w-2 h-2 rounded-full ${
                participant.status === 'active'
                  ? 'bg-green-500'
                  : participant.status === 'completed'
                  ? 'bg-blue-500'
                  : 'bg-gray-400'
              }`}
            />
            <span className="flex-1">{participant.name}</span>
            <span className="text-xs text-gray-500">{participant.status}</span>
          </li>
        ))}
      </ul>

      {participants.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No participants yet
        </p>
      )}
    </div>
  );
}
